import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { JsonLd } from '../components/JsonLd';
import { Seo } from '../components/Seo';
import { PAGE_SEO } from '../lib/seo';
import {
  breadcrumbStructuredData,
  faqStructuredData,
  webPageStructuredData,
} from '../lib/structuredData';

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left hover:text-emerald-600 transition-colors"
      >
        <h2 className="text-lg font-medium text-gray-900 pr-4">{question}</h2>
        <svg
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="pb-5">
          <p className="text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const faqData: FAQItemProps[] = [
  {
    question: "Where does Centmap get its data from?",
    answer: "All data on Centmap, including prices, opening hours, and bar locations are all entirely user-generated. Our editors work hard to make the map as accurate as possible, but errors are certainly possible. It is therefore important to note that bars always have the final say on what prices they charge, regardless of what Centmap says."
  },
  {
    question: "How can I become an editor on Centmap?",
    answer: "The first step to becoming an editor is to create an account via the settings menu within Centmap. You can login with either your Apple ID or Gmail account. Next, you can hit the \"request to become an editor\" button in the settings menu. This will send a notification the app developers who will review your request and make you an editor if all is well. If you don't get approved quickly, we recommend you reach out to the Centmap team at the form below."
  },
  {
    question: "Once I am an editor, how do I add and update bars?",
    answer: "You can add a bar by pressing the large \"+\" button at the top of the main map screen. When adding a bar, you must at least add the bar's name, address, and select a location. You can then use the sliders to add opening times and happy hour times- and add in the beer list below. Be sure to select the right size when adding beers to ensure accurate prices are shown on the map. To edit bars, tap the pencil on the top left of a bar's detail page."
  },
  {
    question: "What if the price on Centmap is different from the actual price at the bar?",
    answer: "While Centmap strives to be as accurate as possible, since all data is user generated we can't guarantee 100% accuracy and it is always up to the bar on how much they want to charge. If you ever find Centmap to be inaccurate- we ask that you become an editor so you can fix it yourself and help out the community!"
  }
];

/**
 * Built from `faqData` itself, never a second copy of the answers: Google
 * requires the marked-up answer to be the one on the page, and two lists would
 * disagree the first time either was edited.
 */
const SCHEMA = [
  webPageStructuredData(PAGE_SEO.faq),
  faqStructuredData(faqData, PAGE_SEO.faq.path),
  breadcrumbStructuredData([{ name: 'FAQ', path: PAGE_SEO.faq.path }]),
];

export const FAQPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Seo {...PAGE_SEO.faq} />
      <JsonLd id="centmap-faq-schema" data={SCHEMA} />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
        <p className="text-gray-600 mb-8">
          Please reach us using the <Link to="/support" className="text-emerald-600 hover:text-emerald-700 underline">contact form</Link> if you cannot find an answer to your question.
        </p>

        <div className="divide-y divide-gray-200 border-t border-gray-200">
          {faqData.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};
